import { Crown, Shield, Zap } from 'lucide-react';
import React from 'react';
import { ContainerBox, FlexContainer, TypographyText } from 'src/tef/components';

interface TierBadgeProps {
  planName: string;
  displayName: string;
  compact?: boolean;
  mini?: boolean;
  className?: string;
}

const TIER_CONFIG: Record<
  string,
  {
    icon: React.ElementType;
    bg: string;
    text: string;
    iconColor: string;
    border: string;
  }
> = {
  enterprise: {
    icon: Crown,
    bg: 'bg-gradient-to-r from-amber-100 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    iconColor: 'text-amber-500 dark:text-amber-400',
    border: 'border-amber-200/60 dark:border-amber-700/40',
  },
  professional: {
    icon: Shield,
    bg: 'bg-primary/10 dark:bg-primary/15',
    text: 'text-primary dark:text-primary-light',
    iconColor: 'text-primary dark:text-primary-light',
    border: 'border-primary/20 dark:border-primary/30',
  },
  starter: {
    icon: Zap,
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-500 dark:text-slate-400',
    iconColor: 'text-slate-400 dark:text-slate-500',
    border: 'border-slate-200 dark:border-slate-700',
  },
};

export default function TierBadge({
  planName,
  displayName,
  compact = false,
  mini = false,
  className = '',
}: TierBadgeProps) {
  const config = TIER_CONFIG[planName] || TIER_CONFIG.starter;
  const Icon = config.icon;

  if (compact) {
    return (
      <ContainerBox
        className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${config.bg} ${className}`}
        title={displayName}
      >
        <Icon className={`w-4 h-4 ${config.iconColor}`} />
      </ContainerBox>
    );
  }

  if (mini) {
    return (
      <FlexContainer
        align="center"
        gap="xs"
        className={`w-fit px-1.5 py-px rounded-md border ${config.border} ${config.bg} shadow-sm ${className}`}
      >
        <Icon className={`w-2.5 h-2.5 flex-shrink-0 ${config.iconColor}`} />
        <TypographyText
          variant={{ size: 'xs' }}
          className={`font-sans font-semibold text-[9px] uppercase tracking-wider leading-tight ${config.text}`}
        >
          {displayName}
        </TypographyText>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer align="center" gap="xs" className={`w-fit px-2 py-0.5 rounded-full ${config.bg} ${className}`}>
      <Icon className={`w-3 h-3 flex-shrink-0 ${config.iconColor}`} />
      <TypographyText
        variant={{ size: 'xs' }}
        className={`font-sans font-medium text-[11px] leading-tight ${config.text}`}
      >
        {displayName}
      </TypographyText>
    </FlexContainer>
  );
}
